import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { EMPTY, Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { UserAccessService } from "./user-access.service";
import { RoleItem } from "@app/models/roleItem";

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private currentUserInfo: any = null;
    private userRoles: any = null;

    constructor(
        private http: HttpClient,
        private userAccessService: UserAccessService) {}

    public setUserRoles(roles: any) {
        this.userRoles = roles;
    }

    public getUserRoles(): any {
        return this.userRoles ? JSON.stringify(this.userRoles) : null;
    }

    public isAuthenticated(): boolean {
        return !!this.currentUserInfo;
    }

    public ensureSession(): Observable<boolean> {
        if (this.currentUserInfo && this.userRoles) {
            return of(true);
        }
        if (this.currentUserInfo?.sub) {
            return this.loadUserRoles(this.currentUserInfo.sub).pipe(
                map(() => true),
                catchError(() => of(false))
            );
        }
        return this.http.get<any>(`${environment.apiUri}/users/current`, { withCredentials: true })
            .pipe(
                switchMap(userInfo => {
                    this.setCurrentUserInfo(userInfo);
                    return this.loadUserRoles(userInfo.sub).pipe(map(() => true));
                }),
                catchError(() => of(false))
            );
    }

    public loadUserRoles(username: string): Observable<RoleItem[]> {
  return this.http
    .get<RoleItem[]>(
      `${environment.apiUri}/users/username/usergroup/${username}`,
      { withCredentials: true }
    )
    .pipe(
      map((roles: RoleItem[]) => {
        this.setUserRoles(roles);
        this.userAccessService.setRoles(roles);
        return roles;
      })
    );
}

    public setCurrentUserInfo(userInfo: any) {
        this.currentUserInfo = userInfo;
    }

    public getCurrentUserInfo(): any {
        return this.currentUserInfo;
    }

    public deleteCurrentUserInfo() {
        this.currentUserInfo = null;
        this.userRoles = null;
    }

    public logout() {
        this.performLocalLogout();
        this.http.post(`${environment.apiUri}/auth/logout`, {}, { withCredentials: true })
            .pipe(catchError(() => EMPTY))
            .subscribe();
    }

    public refresh(): Observable<any> {
        return this.http.post(`${environment.apiUri}/auth/refresh`, {}, { withCredentials: true });
    }

    private performLocalLogout() {
        this.deleteCurrentUserInfo();
        this.userAccessService.clear();

        const keysToKeep = ['lang', 'langVersion'];
        const savedValues = keysToKeep.reduce((acc, key) => {
            const value = localStorage.getItem(key);
            if (value !== null) acc[key] = value;
            return acc;
        }, {} as Record<string, string>);
        
        localStorage.clear();
        
        Object.entries(savedValues).forEach(([key, value]) => {
            localStorage.setItem(key, value);
        });
    }

    public getCurrentUserId() {
        var currentUser = this.getCurrentUserInfo();
        if (currentUser?.jti) {
            return currentUser.jti;
        }
        return "";
    }

    public getCurrentUsername() {
        var currentUser = this.getCurrentUserInfo();
        if (currentUser?.sub) {
            return currentUser.sub;
        }
        return "";
    }

    public getCurrentCashDeskId() {
        var currentUser = this.getCurrentUserInfo();
        if (currentUser?.cashDeskId) {
            return currentUser.cashDeskId;
        }
        return "";
    }

    public getCurrentBranchId() {
        var currentUser = this.getCurrentUserInfo();
        if (currentUser?.branchId) {
            return currentUser.branchId;
        }
        return "";
    }

}
